import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot, faSearch } from "@fortawesome/pro-regular-svg-icons";
import ModalBase from "./ModalBase";
import InputField from "./InputField";
import LoadingSpinner from "./LoadingSpinner";
import { hasValidCoords, loadGoogleMaps } from "../helpers/googleMaps";

const DEFAULT_CENTER = { lat: 62.1983, lng: 17.5672 };
const DEFAULT_ZOOM = 5;
const SELECTED_ZOOM = 17;

const formatCoord = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return "";
    }
    return number.toFixed(6);
};

const geocodeAddress = (address) => {
    if (!address || !window.google?.maps) {
        return Promise.resolve(null);
    }

    const geocoder = new window.google.maps.Geocoder();
    return new Promise((resolve) => {
        geocoder.geocode({ address }, (results, status) => {
            if (status === "OK" && results?.[0]?.geometry?.location) {
                resolve(results[0].geometry.location);
                return;
            }
            resolve(null);
        });
    });
};

const MapLocationPickerModal = ({
    isOpen,
    onClose,
    onConfirm,
    latitude,
    longitude,
    address = "",
}) => {
    const { t } = useTranslation();
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasLoadError, setHasLoadError] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [hasSearchError, setHasSearchError] = useState(false);
    const [selectedPosition, setSelectedPosition] = useState(null);

    const placeMarker = (latLng, panTo = false) => {
        if (!mapRef.current || !window.google?.maps) {
            return;
        }

        const position = {
            lat: latLng.lat(),
            lng: latLng.lng(),
        };

        if (!markerRef.current) {
            markerRef.current = new window.google.maps.Marker({
                map: mapRef.current,
                position,
                draggable: true,
            });
            markerRef.current.addListener("dragend", () => {
                const next = markerRef.current.getPosition();
                if (next) {
                    setSelectedPosition({ lat: next.lat(), lng: next.lng() });
                }
            });
        } else {
            markerRef.current.setPosition(position);
        }

        if (panTo) {
            mapRef.current.panTo(position);
            mapRef.current.setZoom(SELECTED_ZOOM);
        }

        setSelectedPosition(position);
    };

    useEffect(() => {
        if (!isOpen) {
            setIsLoading(true);
            setHasLoadError(false);
            setHasSearchError(false);
            setSearchQuery(address || "");
            setSelectedPosition(null);
            mapRef.current = null;
            markerRef.current = null;
            return undefined;
        }

        setSearchQuery(address || "");
        let cancelled = false;
        let resizeTimeoutId = 0;

        const initMap = async () => {
            try {
                setIsLoading(true);
                setHasLoadError(false);
                await loadGoogleMaps();
                if (cancelled || !mapContainerRef.current) {
                    return;
                }

                let center = DEFAULT_CENTER;
                let zoom = DEFAULT_ZOOM;
                let initialSelected = null;

                if (hasValidCoords(latitude, longitude)) {
                    initialSelected = {
                        lat: Number(latitude),
                        lng: Number(longitude),
                    };
                    center = initialSelected;
                    zoom = SELECTED_ZOOM;
                } else if (address) {
                    const geocoded = await geocodeAddress(address);
                    if (cancelled) {
                        return;
                    }
                    if (geocoded) {
                        center = { lat: geocoded.lat(), lng: geocoded.lng() };
                        zoom = SELECTED_ZOOM;
                    }
                }

                const map = new window.google.maps.Map(mapContainerRef.current, {
                    center,
                    zoom,
                    mapTypeControl: true,
                    streetViewControl: false,
                    fullscreenControl: false,
                    gestureHandling: "greedy",
                });
                mapRef.current = map;

                map.addListener("click", (event) => {
                    if (event.latLng) {
                        placeMarker(event.latLng);
                    }
                });

                if (initialSelected) {
                    placeMarker(new window.google.maps.LatLng(initialSelected.lat, initialSelected.lng));
                }

                resizeTimeoutId = window.setTimeout(() => {
                    if (cancelled || !mapRef.current) {
                        return;
                    }
                    window.google.maps.event.trigger(mapRef.current, "resize");
                    mapRef.current.setCenter(center);
                }, 50);
            } catch {
                if (!cancelled) {
                    setHasLoadError(true);
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        const frame = window.requestAnimationFrame(() => {
            initMap();
        });

        return () => {
            cancelled = true;
            window.cancelAnimationFrame(frame);
            window.clearTimeout(resizeTimeoutId);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init once per open
    }, [isOpen, latitude, longitude, address]);

    const handleSearch = async (event) => {
        event.preventDefault();
        const query = searchQuery.trim();
        if (!query) {
            return;
        }

        setHasSearchError(false);
        const location = await geocodeAddress(query);
        if (!location) {
            setHasSearchError(true);
            return;
        }

        placeMarker(location, true);
    };

    const handleApply = () => {
        if (!selectedPosition) {
            return;
        }

        onConfirm({
            latitude: String(formatCoord(selectedPosition.lat)),
            longitude: String(formatCoord(selectedPosition.lng)),
        });
        onClose();
    };

    return (
        <ModalBase isOpen={isOpen} onClose={onClose} size="lg" className="flex flex-col overflow-y-auto">
            <div className="flex flex-col gap-4">
                <div>
                    <h3 className="text-lg font-bold flex items-center gap-2">
                        <FontAwesomeIcon icon={faLocationDot} className="text-primary-500" />
                        {t("site.pickLocationTitle")}
                    </h3>
                    <p className="text-sm text-mute-500 mt-1">
                        {t("site.pickLocationHelp")}
                    </p>
                </div>

                <form onSubmit={handleSearch} className="flex items-center gap-2">
                    <div className="flex-1">
                        <InputField
                            label={t("common.search")}
                            hideLabel
                            placeholder={t("site.searchAddress")}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <button type="submit" className="btn btn-outline-primary shrink-0">
                        <FontAwesomeIcon icon={faSearch} />
                        {t("common.search")}
                    </button>
                </form>
                {hasSearchError && (
                    <p className="text-xs text-error-500 -mt-2">{t("site.noMatchingPlace")}</p>
                )}

                <div className="relative w-full overflow-hidden rounded-xl border border-mute-200">
                    <div
                        ref={mapContainerRef}
                        className="h-[min(60vh,520px)] w-full"
                    />
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-mute-25/80">
                            <LoadingSpinner text={t("chargeMonitor.loadingMap")} delayMs={0} compact />
                        </div>
                    )}
                    {hasLoadError && (
                        <div className="absolute inset-0 flex items-center justify-center bg-mute-25 p-6 text-center">
                            <p className="text-sm text-error-500">{t("site.failedLoadGoogleMaps")}</p>
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-mute-600">
                        {selectedPosition
                            ? t("site.coordSummary", {
                                lat: formatCoord(selectedPosition.lat),
                                lng: formatCoord(selectedPosition.lng),
                            })
                            : t("site.noLocationSelected")}
                    </p>
                    <div className="flex justify-end gap-2">
                        <button type="button" className="btn btn-outline-primary" onClick={onClose}>
                            {t("common.cancel")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={!selectedPosition}
                            onClick={handleApply}
                        >
                            {t("common.apply")}
                        </button>
                    </div>
                </div>
            </div>
        </ModalBase>
    );
};

export default MapLocationPickerModal;
